import { cx } from '@/utils/cx';

export interface ProgressBarProps {
  /**
   * The current value of the progress bar.
   */
  value: number;
  /**
   * The minimum value of the progress bar.
   * @default 0
   */
  min?: number;
  /**
   * The maximum value of the progress bar.
   * @default 100
   */
  max?: number;
  /**
   * Optional additional CSS class names for the progress bar container.
   */
  className?: string;
  /**
   * Optional additional CSS class names for the progress bar indicator element.
   */
  progressClassName?: string;
  /**
   * Optional function to format the displayed value.
   * It receives the raw value and the calculated percentage.
   */
  valueFormatter?: (
    value: number,
    valueInPercentage: number
  ) => string | number;
  /**
   * Render an animated "working" sweep instead of a fill, for work whose
   * total is unknown (e.g. an import with no upfront estimate). When true,
   * `value` is ignored and `aria-valuenow` is omitted — the ARIA pattern for
   * an indeterminate progressbar.
   * @default false
   */
  indeterminate?: boolean;
}

/**
 * A basic progress bar component.
 */
export const ProgressBarBase = ({
  value,
  min = 0,
  max = 100,
  className,
  progressClassName,
  indeterminate = false,
}: ProgressBarProps) => {
  // Guard against division by zero and clamp to valid range
  const range = max - min;
  const percentage =
    range === 0 ? 0 : Math.max(0, Math.min(100, ((value - min) * 100) / range));

  return (
    <div
      role='progressbar'
      // Omitting aria-valuenow is the ARIA signal for "indeterminate".
      aria-valuenow={indeterminate ? undefined : value}
      aria-valuemin={min}
      aria-valuemax={max}
      className={cx(
        'h-2 w-full overflow-hidden rounded-md bg-quaternary',
        className
      )}
    >
      {indeterminate ? (
        <div
          className={cx(
            'h-full w-2/5 rounded-md bg-fg-brand-primary',
            'motion-safe:animate-[progress-indeterminate_1.4s_ease-in-out_infinite]',
            progressClassName
          )}
        />
      ) : (
        <div
          // Use transform instead of width to avoid layout thrashing (and for smoother animation)
          style={{ transform: `translateX(-${100 - percentage}%)` }}
          className={cx(
            'size-full rounded-md bg-fg-brand-primary transition duration-75 ease-linear',
            progressClassName
          )}
        />
      )}
    </div>
  );
};

type ProgressBarLabelPosition =
  | 'right'
  | 'bottom'
  | 'top-floating'
  | 'bottom-floating';

export interface ProgressIndicatorWithTextProps extends ProgressBarProps {
  /**
   * Specifies the layout of the text relative to the progress bar.
   * - `right`: Text is displayed to the right of the progress bar.
   * - `bottom`: Text is displayed below the progress bar, aligned to the right.
   * - `top-floating`: Text is displayed in a floating tooltip above the progress indicator.
   * - `bottom-floating`: Text is displayed in a floating tooltip below the progress indicator.
   */
  labelPosition?: ProgressBarLabelPosition;
}

/**
 * A progress bar component that displays the value text in various configurable layouts.
 */
export const ProgressBar = ({
  value,
  min = 0,
  max = 100,
  valueFormatter,
  labelPosition,
  className,
  progressClassName,
}: ProgressIndicatorWithTextProps) => {
  // Guard against division by zero and clamp to valid range
  const range = max - min;
  const percentage =
    range === 0 ? 0 : Math.max(0, Math.min(100, ((value - min) * 100) / range));
  const formattedValue = valueFormatter
    ? valueFormatter(value, percentage)
    : `${percentage.toFixed(0)}%`; // Default to rounded percentage

  const baseProgressBar = (
    <ProgressBarBase
      min={min}
      max={max}
      value={value}
      className={className}
      progressClassName={progressClassName}
    />
  );

  switch (labelPosition) {
    case 'right':
      return (
        <div className='flex items-center gap-3'>
          {baseProgressBar}
          <span className='shrink-0 text-sm font-medium text-secondary tabular-nums'>
            {formattedValue}
          </span>
        </div>
      );
    case 'bottom':
      return (
        <div className='flex flex-col items-end gap-2'>
          {baseProgressBar}
          <span className='text-sm font-medium text-secondary tabular-nums'>
            {formattedValue}
          </span>
        </div>
      );
    case 'top-floating':
      return (
        <div className='relative flex flex-col items-end gap-2'>
          {baseProgressBar}
          <div
            style={{ left: `${percentage}%` }}
            className='absolute -top-2 -translate-x-1/2 -translate-y-full rounded-lg bg-primary_alt px-3 py-2 shadow-lg ring-1 ring-secondary_alt'
          >
            <div className='text-xs font-semibold text-secondary tabular-nums'>
              {formattedValue}
            </div>
          </div>
        </div>
      );
    case 'bottom-floating':
      return (
        <div className='relative flex flex-col items-end gap-2'>
          {baseProgressBar}
          <div
            style={{ left: `${percentage}%` }}
            className='absolute -bottom-2 -translate-x-1/2 translate-y-full rounded-lg bg-primary_alt px-3 py-2 shadow-lg ring-1 ring-secondary_alt'
          >
            <div className='text-xs font-semibold text-secondary'>
              {formattedValue}
            </div>
          </div>
        </div>
      );
    default:
      // Fallback or default case, could render the basic progress bar or throw an error
      return baseProgressBar;
  }
};
